// components/personalize/AIInfoStep.tsx
import React, { useState, useEffect, useMemo } from 'react';
import Button from '@/components/Button';
import toast from 'react-hot-toast';
import CustomToast from '@/components/CustomToast';
import { AngleLeftIcon } from '@/utils/svgIcons';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useLoginModal } from '@/contexts/LoginModalContext';
import ImageUploader from './ImageUploader';

export default function AIInfoStep({
  inputValues,
  inputs,
  is_edit,
  UpdateValue,
  setChildName,
  setInputAge,
  step,
  updateProductData,
  handleNextStep,
  setPersonalizeModal,
  childName,
  inputAge,
  childgender,
  savedData,
  setLoadData,
  loadDataLoading,
  user,
  preferences,
  showSavedData,
  setShowSavedData,
  isPostLoginFlow,
  isAIStory,
  croppedImage,
  setCroppedImage,
  imageSelected,
  setImageSelected,
  croppedFile,
  setCroppedFile,
  aiStoryImage,
  aiStoryFile,
  dedicationImageMode,
  setDedicationImageMode,
  setAiStoryImage,
  setAiStoryFile
}: any) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const {t} = useTranslation('personalizeModal');
  const { locale } = useRouter();
  const { setIsOpen, setProductType, setPostLoginCallback } = useLoginModal();

  // FIXED: Safely handle inputs parameter - ensure it's always an array
  const safeInputs = useMemo(() => {
    if (!inputs) return [];
    if (Array.isArray(inputs)) return inputs;
    if (typeof inputs === 'string') return [inputs];
    if (typeof inputs === 'object') {
      // If it's an object, convert keys to array
      if (Object.keys(inputs).length > 0) return Object.keys(inputs);
    }
    return [];
  }, [inputs]);

  // Update the useEffect to use the prop setter
  useEffect(() => {
    // For AI stories, never show saved data selection
    setShowSavedData(false);
  }, [setShowSavedData]);

  // Determine if mobile based on screen size
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const validateAIStoryForm = () => {
    if (!childName || childName.trim() === '') {
      toast.custom((tEl) => <CustomToast type='error' t={tEl} message={`${t('enterChildNameNotebook')}`} />);
      return false;
    }
    
    if (!childgender || childgender === '') {
      toast.custom((tEl) => <CustomToast type='error' t={tEl} message={`${t('enterChildGenderError')}`} />);
      return false;
    }
    
    if (!aiStoryImage || aiStoryImage === null) {
      toast.custom((tEl) => <CustomToast type='error' t={tEl} message={`${t('aiStoryImageRequired')}`} />);
      return false;
    }
    
    return true;
  };

  return (
    <div className="flex-1 overflow-y-auto px-4 py-6 space-y-6 bg-[#FFFCEB] h-full" id='ai-info-step' dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className='lg:mx-auto lg:max-w-xl' dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}>
        <div className="inputs flex flex-col gap-4 items-center mt-6">
          {/* FIXED: Use safeInputs to iterate over input values */}
          {safeInputs.map((key: string) => {
            const value = inputValues[key];
            const isEmpty = value === null || value === undefined || 
            (typeof value === 'string' && value.trim() === '');

            if (isEmpty && is_edit === '1') {
              return null;
            }

            return (
              <div key={key} className="w-full form-group" dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}>
                <label className={`block text-xs ${locale === 'ar' ? 'text-right' : 'text-left'}`}>
                  {key === 'name' && `${t('infoStepEnterName')}`}
                  {key === 'first_name' && `${t('infoStepEnterFirstName')}`}
                  {key === 'age' && `${t('infoStepEnterAge')}`}
                  {key === 'init' && `${t('infoStepEnterInitial')}`}
                </label>
                <input
                  type={key === 'age' ? 'number' : 'text'}
                  name={key}
                  min="1"
                  max="100"
                  onChange={e => {
                    const val = e.target.value;
                    UpdateValue(key, val);
                    if (key === 'name') setChildName(val);
                    if (key === 'age') setInputAge(val);
                  }}
                  dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}
                  value={inputValues[key] ?? ''}
                  className="border-1 text-primary border-gray-300 w-full font-semibold placeholder:font-light mt-2 placeholder:opacity-70 py-3 text-xs px-3 focus:ring-1 transition duration-200 focus:ring-black focus:border-transparent rounded-md"
                  disabled={loadDataLoading}
                />
                {
                  key === 'name' && 
                  <p className='mt-2 text-md font-light text-right'>{t('enterChildNameInfoText')}</p>
                }
              </div>
            );
          })}
          
          {/* Image Upload for AI Stories - This is for STORY GENERATION */}
          <div className="w-full mt-4">
            <ImageUploader
              croppedImage={aiStoryImage}  // Use aiStoryImage for the story
              setCroppedImage={setAiStoryImage}
              imageSelected={imageSelected}
              setImageSelected={setImageSelected}
              uuid="temp-ai-story"
              product={null}
              croppedFile={aiStoryFile}
              setCroppedFile={setAiStoryFile}
              aiStory={true}
              forAiStory={true}  // This is for AI story generation
              isDedicationStep={false}
              aiStoryImage={aiStoryImage}
              dedicationImageMode={dedicationImageMode}
              setDedicationImageMode={setDedicationImageMode}
            />
            <p className="text-sm text-gray-500 mt-2">
              {t('aiStoryImageUploadHelp')}
            </p>
          </div>
        </div>
      </div>

      {step === 'info' && (
        <div className='hidden lg:flex flex-col gap-4 items-center justify-center mx-auto bg-white px-4 py-6 max-w-3xl'>
          <button 
            className="w-full lg:max-w-xl text-white p-4 rounded-md bg-primary font-medium"
            id='ai-info-step-continue' 
            onClick={() => {
              let isValid = false;
              
              isValid = validateAIStoryForm();
              
              if (!isValid) {
                return;
              }
              
              updateProductData({
                name: childName, 
                age: inputAge, 
                gender: childgender
              });
              
              handleNextStep();
            }}
            disabled={loadDataLoading}
          >
            {loadDataLoading ? `${t('infoStepDataLoadingText')}` : 
             `${t('aiStoryGenerateButton')}`}
          </button>
          <button 
            className={`mt-2 text-primary-500 text-sm font-medium flex items-center justify-center w-full text-center`} 
            onClick={() => setPersonalizeModal(false)}
            disabled={loadDataLoading}
          >
            {t('infoStepBackButton')} 
            <span className={`${locale === 'ar' ? 'mr-3' : 'ml-3 rotate-180'}`}>
              <AngleLeftIcon/>
            </span>
          </button>
        </div>
      )}
    </div>
  );
}